import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { typedInfiniteQuery, typedMutation, typedQuery } from "shared/api/types/typedQueries";
import useToastes from 'shared/hooks/useToastes';
import useTypedHistory from 'shared/hooks/useTypedHistory';
import { PathsE, PathsParams, LayoutsE } from 'shared/routes';
import { UserResourcesT, ChangeStatusUserResourceT, CreateUserResourceT, DeleteUserResourceT, OneUserResourceT, UpdateUserResourceT } from '../types/user-resources';
import { instance } from "./common";

const BASE_URL = 'user-resources'

const USER_RESOURCES_KEY = 'USER_RESOURCES_KEY';
const ONE_USER_RESOURCE_KEY = 'ONE_USER_RESOURCE_KEY';

export const useGetUserResources = typedInfiniteQuery<UserResourcesT>(({ params }) => (
  useInfiniteQuery({
    queryKey: [USER_RESOURCES_KEY, params],
    queryFn: () => instance.get<unknown, UserResourcesT['response']>(BASE_URL, { params }),
    initialPageParam: 1,
    getNextPageParam: () => null,
    getPreviousPageParam: () => null,
  })
));

export const useGetOneUserResource = typedQuery<OneUserResourceT>(({ pathParams: { resourceId } }) => (
  useQuery({
    queryKey: [ONE_USER_RESOURCE_KEY, resourceId],
    queryFn: () => instance.get(`${BASE_URL}/${resourceId}`),
    enabled: !!resourceId,
  })
));

export const useCreateUserResource = typedMutation<CreateUserResourceT>(() => {
  const queryCache = useQueryClient();
  const { successToast } = useToastes();
  const push = useTypedHistory()
  return (
    useMutation({
      mutationFn: (data) => instance.post(BASE_URL, data),
      onSuccess: () => {
        successToast('UserResource was successfully created');
        push(PathsE.USER_RESOURCES);
        queryCache.invalidateQueries({ queryKey: [USER_RESOURCES_KEY] });
      }
    })
  )
});

export const useUpdateOneUserResource = typedMutation<UpdateUserResourceT>(({ pathParams: { resourceId } }) => {
  const { successToast } = useToastes();
  const queryCache = useQueryClient()
  const push = useTypedHistory<PathsParams[PathsE.SHOW_USER_RESOURCE]>();
  return (
    useMutation({
      mutationFn: (data) => instance.put(`${BASE_URL}/${resourceId}`, data),
      onSuccess: () => {
        successToast('UserResource was successfully updated');
        queryCache.invalidateQueries({ queryKey: [USER_RESOURCES_KEY] });
        queryCache.invalidateQueries({ queryKey: [ONE_USER_RESOURCE_KEY, resourceId] })
        push(PathsE.SHOW_USER_RESOURCE, LayoutsE.DASHBOARD, { resourceId })
      }
    })
  )
});

export const useActivateOneUserResource = typedMutation<ChangeStatusUserResourceT>(() => {
  const { successToast } = useToastes();
  const queryCache = useQueryClient()
  return (
    useMutation({
      mutationFn: ({ id }) => instance.put(`${BASE_URL}/${id}`, { isActive: true }),
      onSuccess: (_, { id }) => {
        successToast('UserResource was successfully activated');
        queryCache.invalidateQueries({ queryKey: [USER_RESOURCES_KEY] });
        queryCache.invalidateQueries({ queryKey: [ONE_USER_RESOURCE_KEY, id] });
      }
    })
  )
});

export const useDeactivateOneUserResource = typedMutation<ChangeStatusUserResourceT>(() => {
  const { successToast } = useToastes();
  const queryCache = useQueryClient()
  return (
    useMutation({
      mutationFn: ({ id }) => instance.put(`${BASE_URL}/${id}`, { isActive: false }),
      onSuccess: (_, { id }) => {
        successToast('UserResource was successfully deactivated');
        queryCache.invalidateQueries({ queryKey: [USER_RESOURCES_KEY] });
        queryCache.invalidateQueries({ queryKey: [ONE_USER_RESOURCE_KEY, id] });
      }
    })
  )
});

export const useDeleteUserResource = typedMutation<DeleteUserResourceT>(({ pathParams: { resourceId } }) => {
  const { successToast } = useToastes();
  const queryCache = useQueryClient()
  const push = useTypedHistory();
  return (
    useMutation({
      mutationFn: () => instance.delete(`${BASE_URL}/${resourceId}`),
      onSuccess: () => {
        successToast('User resource was successfully deleted');
        queryCache.invalidateQueries({ queryKey: [USER_RESOURCES_KEY] });
        push(PathsE.USER_RESOURCES, LayoutsE.DASHBOARD)
      }
    })
  )
});
